import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { GameMode, GameState, GameWithDate } from '../types/types';
import { demoData } from '../gameData/data';
import {
  getLastDayPlayed,
  getGameStateFromSetup,
  getPlayerStreakStatistics,
  isGameOver,
  NUMBER_OF_HINTS,
  updateGameFinishedOnDate,
} from '../helpers';
import {
  loadGameFromLocalStorage,
  saveGameToLocalStorage,
  saveStatsToLocalStorage,
} from '../helpers/localStorage';
import { getSolvedPuzzle } from '../helpers/gameSolver';
import {
  useCurrentGame,
  useGameByDate,
  usePostStatistics,
  useStatisticsForDate,
} from '../helpers/useApi';
import { GetStatisticsResponse, StatisticsRequest } from '../helpers/api';
import { useModal } from './ModalContext';
import { dateToDateString } from '../helpers/dates';

interface GameContextValue {
  mode: GameMode;
  gameSetupData?: GameWithDate;
  gameState?: GameState;
  setGameState: React.Dispatch<React.SetStateAction<GameState | undefined>>;
  playGameSplash: () => void;
  isLoading: boolean;
  statsData?: GetStatisticsResponse;
  isPostingStats: boolean;
  statsError: boolean;
  statsRequest?: StatisticsRequest;
  setGameDate: (date?: Date) => void;
  setMode: React.Dispatch<React.SetStateAction<GameMode>>;
  isArchiveGame?: boolean;
}

const GameContext = createContext<GameContextValue | undefined>(undefined);

export function GameProvider({ children }: { children: React.ReactNode }) {
  const [date, setGameDate] = useState<Date>();
  const [mode, setMode] = useState<GameMode>('splash');
  const [gameState, setGameState] = useState<GameState>();
  const [gameSetupData, setGameSetupData] = useState<GameWithDate>();
  const [startTime, setStartTime] = useState<number>(0);
  const [shouldPostStatistics, setShouldPostStatistics] = useState(true);
  const [statisticsRequest, setStatisticsRequest] = useState<
    StatisticsRequest | undefined
  >(undefined);

  const { openModal, closeModal } = useModal();

  // --- API hooks ---
  const currentGame = useCurrentGame();
  const gameByDate = useGameByDate(date);
  const postStatistics = usePostStatistics();
  const fetchComparativeStats = useStatisticsForDate();

  const isPostingStats = postStatistics.isPending;
  const statsError = postStatistics.isError;

  const isArchiveGame =
    date !== undefined && currentGame.data?.date !== dateToDateString(date);
  const activeGame = isArchiveGame ? gameByDate : currentGame;
  const { data, isLoading } = activeGame;

  // --- Initialize game when API data changes ---
  useEffect(() => {
    if (!data) return;
    closeModal();
    initGameState();
    if (!getLastDayPlayed()) openModal('help');
  }, [data, date]);

  function initGameState() {
    if (!data) return;
    setGameSetupData(data);
    loadGame(data);
    setStatisticsRequest(undefined);
    const local = loadGameFromLocalStorage(data.date);
    if (local) {
      setMode(isArchiveGame ? 'archive' : 'real');
    }
  }

  // --- Handle mode-based initialization ---
  useEffect(() => {
    if (mode === 'demo') {
      closeModal();
      setGameSetupData(demoData);
      loadGame(demoData);
    }
    if (mode === 'splash') {
      initGameState();
    }
  }, [mode]);

  // --- Auto-save progress ---
  useEffect(() => {
    if (gameState && gameSetupData && (mode === 'real' || mode === 'archive')) {
      saveGameToLocalStorage(gameSetupData.date, gameState, startTime);
    }
  }, [gameState, gameSetupData, mode, startTime]);

  // --- Detect win state ---
  useEffect(() => {
    if (gameState && isGameOver(gameState)) {
      onWinGame();
    }
  }, [gameState]);

  // --- Debug/Dev helper: window.Wormle.solvePuzzle ---
  useEffect(() => {
    if (!gameSetupData?.game) return;
    window.Wormle = window.Wormle || {};
    window.Wormle.solvePuzzle = () => {
      setShouldPostStatistics(false);
      setGameState(getSolvedPuzzle(gameSetupData.game).solvedGameState);
    };
  }, [gameSetupData]);

  // --- Core load logic ---
  const loadGame = useCallback((gameData: GameWithDate) => {
    const local = loadGameFromLocalStorage(gameData.date);
    if (!local) {
      setGameState(getGameStateFromSetup(gameData.game));
      setStartTime(Date.now());
      return;
    }
    setGameState(local.gameState);
    setStartTime(local.startDate);
  }, []);

  // --- Handle play button from splash/archive ---
  const playGameSplash = useCallback(() => {
    setMode(isArchiveGame ? 'archive' : 'real');
  }, [isArchiveGame]);

  // --- Win handler ---
  const onWinGame = useCallback(async () => {
    if (!gameSetupData || !gameState) return;

    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const hasPlayedToday = getLastDayPlayed() === gameSetupData.date;

    if (mode === 'real' && shouldPostStatistics && !hasPlayedToday) {
      updateGameFinishedOnDate(gameSetupData.date);
      await postAndFetchStatistics();
    }

    openModal('win');
  }, [gameSetupData, gameState, mode, shouldPostStatistics, openModal]);

  // --- Statistics posting ---
  const postAndFetchStatistics = useCallback(async () => {
    if (!gameSetupData || !gameState || mode !== 'real') return;

    const stats = getPlayerStreakStatistics();
    const seconds = (Date.now() - startTime) / 1000;

    const request: StatisticsRequest = {
      hintsUsed: NUMBER_OF_HINTS - gameState.hintsRemaining,
      moveCount: gameState.moveCount,
      secondsToComplete: seconds,
      streak: stats.currentStreak,
    };

    try {
      await postStatistics.mutateAsync({
        date: new Date(gameSetupData.date),
        statistics: request,
      });

      setStatisticsRequest(request);

      const comparativeStats = await fetchComparativeStats.mutateAsync({
        date: new Date(gameSetupData.date),
        stats: request,
      });

      saveStatsToLocalStorage(
        gameSetupData.date,
        comparativeStats,
        seconds,
        NUMBER_OF_HINTS - gameState.hintsRemaining,
      );
    } catch (err) {
      console.error('Failed to post or fetch statistics:', err);
    }
  }, [
    gameSetupData,
    gameState,
    mode,
    postStatistics,
    fetchComparativeStats,
    startTime,
  ]);

  const statsData = mode === 'real' ? fetchComparativeStats.data : undefined;

  const value = useMemo<GameContextValue>(
    () => ({
      mode,
      gameSetupData,
      gameState,
      setGameState,
      playGameSplash,
      isLoading,
      statsData,
      isPostingStats,
      statsError,
      setGameDate,
      statsRequest: statisticsRequest,
      setMode,
      isArchiveGame,
    }),
    [
      mode,
      gameSetupData,
      gameState,
      isLoading,
      statsData,
      isPostingStats,
      statsError,
      statisticsRequest,
      isArchiveGame,
    ],
  );

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used within GameProvider');
  return ctx;
}
